import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, Building2, Mail, Calendar, 
  User, AlertCircle, Loader2, ClipboardList
} from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import superAdminAuditService from '../../../../../services/audit_services/superAdminAuditService';

export default function AllAuditProgramDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [programData, setProgramData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchProgramDetails();
    }
  }, [id]);

  const fetchProgramDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminAuditService.getAuditProgramDetails(authTokens.access, id);
      setProgramData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'draft': 'bg-gray-100 text-gray-700',
      'open': 'bg-blue-100 text-blue-700',
      'closed': 'bg-red-100 text-red-700',
      'achieved': 'bg-green-100 text-green-700',
      'unachieved': 'bg-orange-100 text-orange-700'
    };

    const colorClass = statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
        {status === 'draft' ? 'Brouillon' : 
         status === 'open' ? 'Ouvert' : 
         status === 'closed' ? 'Fermé' : 
         status === 'achieved' ? 'Réalisé' : 
         status === 'unachieved' ? 'Non réalisé' : status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!programData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée trouvée</p>
        </div>
      </div>
    );
  }

  const { program, organization, created_by, last_updated_by, audit_plans, statistics } = programData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/audit-programs')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{program.title}</h1>
            <p className="text-xs text-gray-500">Détails du programme d'audit</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {getStatusBadge(program.status)}
        </div>
      </div>

      <div className="space-y-6">
        {/* Program Information Card */}
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center mb-4">
            <FileText className="h-5 w-5 text-purple-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations du programme</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Titre du programme</label>
                  <p className="text-sm text-gray-900">{program.title}</p>
                </div>
                {program.reference && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Référence</label>
                    <p className="text-sm text-gray-900">{program.reference}</p>
                  </div>
                )}
                {program.objective && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Objectif</label>
                    <p className="text-sm text-gray-900">{program.objective}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Statut</label>
                  <div className="mt-1">
                    {getStatusBadge(program.status)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Créé le</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(program.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                {program.updated_at && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Mis à jour le</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(program.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                )}
                {program.closed_date && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Date de fermeture</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(program.closed_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                )}
                {program.achieved_date && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Date de réalisation</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(program.achieved_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Approvers within Program Info */}
          {program.approvers && program.approvers.length > 0 && (
            <div className="mt-6 pt-6 border-t ">
              <div className="flex items-center mb-3">
                <User className="h-4 w-4 text-gray-600 mr-2" />
                <h3 className="text-xs font-semibold text-gray-900">Approbateurs ({program.approvers.length})</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {program.approvers.map((approver) => (
                  <div key={approver.id} className="p-3 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">
                      {approver.user.full_name || `${approver.user.first_name} ${approver.user.last_name}`.trim() || 'Inconnu'}
                    </p>
                    {approver.user.email && (
                      <div className="flex items-center mt-1">
                        <Mail className="h-3 w-3 text-gray-400 mr-1" />
                        <p className="text-xs text-gray-600">{approver.user.email}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scopes within Program Info */}
          {program.scopes && program.scopes.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center mb-3">
                <ClipboardList className="h-4 w-4 text-gray-600 mr-2" />
                <h3 className="text-xs font-semibold text-gray-900">Périmètre ({program.scopes.length})</h3>
              </div>
              <div className="space-y-2">
                {program.scopes.map((scope) => (
                  <div key={scope.id} className="p-3 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-900">{scope.scope}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Parties within Program Info */}
          {program.parties && program.parties.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center mb-3">
                <User className="h-4 w-4 text-gray-600 mr-2" />
                <h3 className="text-xs font-semibold text-gray-900">Parties prenantes ({program.parties.length})</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {program.parties.map((party) => (
                  <div key={party.id} className="p-3 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        party.role === 'auditor' ? 'bg-blue-100 text-blue-700' :
                        party.role === 'auditee' ? 'bg-green-100 text-green-700' :
                        party.role === 'approver' ? 'bg-purple-100 text-purple-700' :
                        party.role === 'participant' ? 'bg-gray-100 text-gray-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {party.role === 'auditor' ? 'Auditeur' :
                         party.role === 'auditee' ? 'Audité' :
                         party.role === 'approver' ? 'Approbateur' :
                         party.role === 'participant' ? 'Participant' : party.role}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {party.user.full_name || `${party.user.first_name} ${party.user.last_name}`.trim() || 'Inconnu'}
                    </p>
                    {party.user.email && (
                      <div className="flex items-center mt-1">
                        <Mail className="h-3 w-3 text-gray-400 mr-1" />
                        <p className="text-xs text-gray-600">{party.user.email}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Organization Information Card */}
        {organization && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Nom de l'organisation</label>
                    <p className="text-sm text-gray-900">{organization.name}</p>
                  </div>
                  {organization.description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Description</label>
                      <p className="text-sm text-gray-900">{organization.description}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Statut d'abonnement</label>
                    <div className="mt-1">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        organization.is_subscribed 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {organization.is_subscribed ? 'Abonné' : 'Non abonné'}
                      </span>
                    </div>
                  </div>
                  {organization.created_at && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Organisation créée le</label>
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                        <p className="text-sm text-gray-900">
                          {new Date(organization.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Creator and Last Updated By Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {created_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Créé par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {created_by.full_name || `${created_by.first_name} ${created_by.last_name}`.trim() || 'Inconnu'}
                </p>
                {created_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{created_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {last_updated_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Dernière modification par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {last_updated_by.full_name || 'Inconnu'}
                </p>
                {last_updated_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{last_updated_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Audit Plans Card */}
        {audit_plans && audit_plans.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ClipboardList className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Plans d'audit associés</h2>
              </div>
              <div className="text-xs text-gray-500">
                {audit_plans.length} plan(s)
              </div>
            </div>

            <div className="space-y-3">
              {audit_plans.map((plan) => (
                <div key={plan.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 mb-1">{plan.title}</p>
                      <div className="flex items-center space-x-3 text-xs text-gray-500 mb-2">
                        <span className={`inline-block px-2 py-1 rounded font-medium ${
                          plan.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                          plan.status === 'released' ? 'bg-blue-100 text-blue-700' :
                          plan.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                          plan.status === 'completed' ? 'bg-green-100 text-green-700' :
                          plan.status === 'closed' ? 'bg-red-100 text-red-700' :
                          plan.status === 'achieved' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {plan.status === 'draft' ? 'Brouillon' :
                           plan.status === 'released' ? 'Publié' :
                           plan.status === 'in_progress' ? 'En cours' :
                           plan.status === 'completed' ? 'Terminé' :
                           plan.status === 'closed' ? 'Fermé' :
                           plan.status === 'achieved' ? 'Réalisé' : plan.status}
                        </span>
                        <span>•</span>
                        <span>{new Date(plan.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      {plan.created_by && (
                        <p className="text-xs text-gray-600">
                          Créé par: {plan.created_by.name || plan.created_by.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistics Card */}
        {statistics && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <ClipboardList className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Statistiques</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_approvers}</p>
                <p className="text-xs text-gray-500 mt-1">Approbateurs</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_scopes}</p>
                <p className="text-xs text-gray-500 mt-1">Périmètres</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_parties}</p>
                <p className="text-xs text-gray-500 mt-1">Parties prenantes</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_plans}</p>
                <p className="text-xs text-gray-500 mt-1">Plans d'audit</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{statistics.plans_by_status.achieved}</p>
                <p className="text-xs text-gray-500 mt-1">Plans réalisés</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
